import { translate } from '@avensia/excite';
import { styled } from '@glitz/react';
import * as React from 'react';
import { translationExists } from 'webapp/utils/translations';
import { Variant } from 'webapp/product/price-variant';
import { createMaxMobileViewportDecorator } from 'webapp/viewport';
import { useFormatPrice } from '@avensia/excite-ui';
import { NnTestIdProps } from 'webapp/shared/test-ids';
import { a11yGrey } from 'webapp/shared/sharedTheme';

export type PriceConfig = {
  showRedPrice?: boolean;
  vatActive?: boolean;
  showComparePricePlp?: boolean;
  showComparePriceType?: NordicNest.Api.ComparePriceType;
  showComparePriceStrikethrough?: boolean;
  showComparePriceSuffix?: boolean;
  lowestHistoricalPrice?: Excite.Commerce.AmountWithVatApiModel;
  showComparePricePlpType?: NordicNest.Api.ComparePricePlpType;
  showOriginalPrice?: boolean;
};

type PropType = {
  price: { discountGtmPrice?: string } & (NordicNest.Api.PriceApiModel | Excite.Commercetools.CartPriceApiModel);
  variant?: Variant;
  config?: PriceConfig;
  hideOriginalPrice?: boolean;
  useOrigianlPricePrefixSuffix?: boolean;
  prefix?: string;
  quantity?: number;
  productCardStyling?: boolean;
};

const Price = styled(
  ({
    price,
    variant = Variant.Default,
    config,
    hideOriginalPrice = false,
    useOrigianlPricePrefixSuffix,
    prefix,
    productCardStyling,
  }: PropType) => {
    const showVatText = variant === Variant.Default;

    const shouldShowOriginalPrefixSuffix =
      config?.showComparePricePlpType === 'originalPrice' ||
      (config?.showComparePricePlpType === 'inherit' && config?.showComparePriceType === 'originalPrice');

    let originalPricePrefix =
      useOrigianlPricePrefixSuffix &&
      config?.showComparePriceSuffix &&
      shouldShowOriginalPrefixSuffix &&
      translate(t => t.productListing.originalPricePrefix);
    if (originalPricePrefix && !translationExists(originalPricePrefix)) {
      originalPricePrefix = '';
    }
    let originalPriceSuffix =
      useOrigianlPricePrefixSuffix &&
      config?.showComparePriceSuffix &&
      shouldShowOriginalPrefixSuffix &&
      translate(t => t.productListing.originalPriceSuffix);
    if (originalPriceSuffix && !translationExists(originalPriceSuffix)) {
      originalPriceSuffix = '';
    }

    const showComparePrice = !hideOriginalPrice && config?.showComparePricePlp;
    let comparePrice =
      config?.showComparePriceType === 'originalPrice' ? price?.regular : config?.lowestHistoricalPrice;

    if (config?.showComparePricePlpType === 'originalPrice') {
      comparePrice = price?.regular;
    }

    if (config?.showComparePricePlpType === 'referencePrice') {
      comparePrice = config?.lowestHistoricalPrice;
    }

    const formatPrice = useFormatPrice();
    return (
      <Base css={priceDecorator(variant, productCardStyling)}>
        <CurrentPrice
          css={config?.showRedPrice && discountDecorator}
          {...(config?.showRedPrice ? NnTestIdProps.RedPriceElement : NnTestIdProps.CurrentPriceElement)}
          suppressHydrationWarning
        >
          {prefix && prefix.length > 0 ? (
            <>
              <Prefix>{prefix} </Prefix>
              {formatPrice(price.current.inclVat)}
            </>
          ) : (
            formatPrice(price.current.inclVat)
          )}
        </CurrentPrice>
        {showComparePrice && comparePrice && (
          <OriginalPrice css={variant === Variant.Default && originalPriceDecorator}>
            {Boolean(originalPricePrefix) && (
              <OriginalPricePrefixSuffix>{originalPricePrefix + ' '}</OriginalPricePrefixSuffix>
            )}
            <OriginalPriceSum
              css={config?.showComparePriceStrikethrough && !config?.showOriginalPrice && comparePriceSumDecorator}
              suppressHydrationWarning
              {...NnTestIdProps.ComparePriceElement}
            >
              {formatPrice(comparePrice.inclVat)}
            </OriginalPriceSum>
            {Boolean(originalPriceSuffix) && (
              <OriginalPricePrefixSuffix {...NnTestIdProps.OriginalPricePrefixSuffix}>
                {' ' + originalPriceSuffix}
              </OriginalPricePrefixSuffix>
            )}
          </OriginalPrice>
        )}
        {showVatText && (
          <VatText>
            {!hideOriginalPrice &&
              config?.showComparePricePlp &&
              config?.showComparePriceSuffix &&
              (config?.vatActive
                ? translate(t => t.order.recommendedRetailPriceInclVat)
                : translate(t => t.order.recommendedRetailPriceExclVat))}
          </VatText>
        )}
      </Base>
    );
  },
);

export default Price;

const Base = styled.div({
  display: 'flex',
  columnGap: t => t.spacing.xs,
  alignItems: 'baseline',
});

const Prefix = styled.span({
  fontWeight: '350',
});
const discountDecorator = styled({
  color: t => t.price?.discount?.color,
});

function priceDecorator(variant: Variant, productCardStyling?: boolean) {
  switch (variant) {
    case Variant.Default:
      return styled({
        flexDirection: 'column',
        justifyContent: 'left',
        fontSize: t => t.price?.current?.page?.fontSize,
      });
    case Variant.CompactReverse:
      return styled({
        flexDirection: 'row-reverse',
        fontSize: t => t.price?.current?.card?.fontSize,
        fontWeight: t => t.price?.current?.card?.fontWeight,
      });
    case Variant.Compact:
      return styled(
        {
          fontWeight: t => t.price?.current?.card?.fontWeight,
          fontSize: t => t.price?.current?.card?.fontSize,
        },
        productCardStyling && createMaxMobileViewportDecorator({ flexDirection: 'column' }),
      );
  }
}

const CurrentPrice = styled.span();

const OriginalPrice = styled.span({
  color: a11yGrey,
  fontWeight: t => t.price?.original?.fontWeight,
  fontSize: t => t.price?.original?.fontSize,
});

const originalPriceDecorator = styled({
  fontSize: t => t.fontSize.m,
});

const OriginalPriceSum = styled.span();

const comparePriceSumDecorator = styled({
  textDecoration: 'line-through',
});

const OriginalPricePrefixSuffix = styled.span({
  textDecoration: 'none',
});

const VatText = styled.span({
  color: t => t.dark.color,
  fontWeight: t => t.price?.vatText?.fontWeight,
});
